import React, { useEffect, useMemo, useState } from "react";
import { api, JOB_STATUSES } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format, addDays, addMonths, startOfWeek, startOfMonth, endOfMonth, isSameDay, isSameMonth } from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Schedule({ compact = false }) {
  const [jobs, setJobs] = useState([]);
  const [providers, setProviders] = useState([]);
  const [view, setView] = useState(compact ? "week" : "week"); // today, tomorrow, week, month
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [monthAnchor, setMonthAnchor] = useState(startOfMonth(new Date()));

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data));
    api.get("/jobs").then((r) => setJobs(r.data));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const filtered = jobs.filter((j) => {
    if (provider && j.assigned_provider_id !== provider) return false;
    if (status) {
      if (j.status !== status) return false;
    } else if (j.status === "Cancelled") {
      // Hide cancelled jobs from the active calendar unless explicitly filtered
      return false;
    }
    return true;
  });

  const today = new Date();
  const tomorrow = addDays(today, 1);

  const jobsOn = (d) => filtered.filter((j) => j.date === format(d, "yyyy-MM-dd"))
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));

  const providerName = (id) => providers.find((p) => p.id === id)?.name || "Unassigned";

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">Schedule</h1>
          <p className="text-sm text-slate-500 mt-1">Jobs at a glance.</p>
          <div className="flex flex-wrap gap-2 mt-2 text-[11px]" data-testid="schedule-legend">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#E8F5E0] text-[#1F4D0F] ring-1 ring-[#5BA83F]/40"><span className="w-2 h-2 rounded-full bg-[#5BA83F]" />Confirmed</span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 ring-1 ring-amber-300"><span className="w-2 h-2 rounded-full bg-amber-500" />Payment pending</span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-600 text-white"><span className="w-2 h-2 rounded-full bg-white" />Completed</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {["today","tomorrow","week","month"].map((v) => (
            <Button key={v} size="sm" variant={view === v ? "default" : "outline"} onClick={() => setView(v)}
              className={view === v ? "bg-[#1B3A6B] text-white capitalize" : "capitalize"} data-testid={`schedule-view-${v}`}>{v}</Button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <Select value={provider} onValueChange={(v) => setProvider(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52"><SelectValue placeholder="All cleaners" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cleaners</SelectItem>
            {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {JOB_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {view === "today" && <DayList title="Today" date={today} jobs={jobsOn(today)} providerName={providerName} />}
      {view === "tomorrow" && <DayList title="Tomorrow" date={tomorrow} jobs={jobsOn(tomorrow)} providerName={providerName} />}

      {view === "week" && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <Button size="sm" variant="outline" onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft className="w-4 h-4" /></Button>
            <div className="text-sm font-medium text-slate-600">
              {format(days[0], "MMM d")} – {format(days[6], "MMM d, yyyy")}
            </div>
            <Button size="sm" variant="outline" onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
            {days.map((d) => {
              const dayJobs = jobsOn(d);
              const isToday = isSameDay(d, new Date());
              return (
                <div key={d.toISOString()} className={`bg-white rounded-xl ring-1 p-3 min-h-[180px] ${isToday ? "ring-[#5BA83F]" : "ring-slate-200"}`} data-testid={`schedule-day-${format(d,"yyyy-MM-dd")}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className={`text-xs uppercase tracking-wide font-bold ${isToday ? "text-[#3F7A1F]" : "text-slate-500"}`}>{format(d, "EEE")}</div>
                    <div className={`text-lg font-heading font-bold ${isToday ? "text-[#1B3A6B]" : "text-slate-700"}`}>{format(d, "d")}</div>
                  </div>
                  <div className="space-y-2">
                    {dayJobs.map((j) => (
                      <JobCard key={j.id} job={j} providerName={providerName} />
                    ))}
                    {dayJobs.length === 0 && <p className="text-xs text-slate-400">No jobs</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "month" && (
        <MonthGrid
          monthAnchor={monthAnchor}
          setMonthAnchor={setMonthAnchor}
          jobsOn={jobsOn}
          providerName={providerName}
        />
      )}
    </div>
  );
}

function MonthGrid({ monthAnchor, setMonthAnchor, jobsOn, providerName }) {
  const gridStart = startOfWeek(startOfMonth(monthAnchor), { weekStartsOn: 1 });
  const cells = useMemo(() => {
    // Always render 6 weeks × 7 = 42 cells so grid is stable and covers month edges
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [gridStart]);
  const monthEnd = endOfMonth(monthAnchor);
  const today = new Date();

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <Button size="sm" variant="outline" onClick={() => setMonthAnchor(addMonths(monthAnchor, -1))} data-testid="schedule-month-prev">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="text-sm font-medium text-slate-700" data-testid="schedule-month-label">
          {format(monthAnchor, "MMMM yyyy")}
        </div>
        <Button size="sm" variant="outline" onClick={() => setMonthAnchor(addMonths(monthAnchor, 1))} data-testid="schedule-month-next">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-wide font-bold text-slate-500 text-center">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5" data-testid="schedule-month-grid">
        {cells.map((d) => {
          const dayJobs = jobsOn(d);
          const inMonth = isSameMonth(d, monthAnchor);
          const isToday = isSameDay(d, today);
          const isPast = d < today && !isToday && d < monthEnd;
          return (
            <div
              key={d.toISOString()}
              className={`bg-white rounded-lg ring-1 p-1.5 min-h-[90px] transition ${
                isToday
                  ? "ring-[#5BA83F] shadow-sm"
                  : inMonth
                    ? "ring-slate-200"
                    : "ring-slate-100 bg-slate-50/50 opacity-60"
              }`}
              data-testid={`schedule-month-day-${format(d, "yyyy-MM-dd")}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`text-xs font-bold ${
                  isToday ? "text-[#1B3A6B]" : isPast ? "text-slate-400" : "text-slate-600"
                }`}>{format(d, "d")}</div>
                {dayJobs.length > 0 && (
                  <span className="text-[9px] bg-[#1B3A6B] text-white rounded-full px-1.5 py-0 font-bold">{dayJobs.length}</span>
                )}
              </div>
              <div className="space-y-1">
                {dayJobs.slice(0, 3).map((j) => (
                  <JobCard key={j.id} job={j} providerName={providerName} />
                ))}
                {dayJobs.length > 3 && (
                  <div className="text-[10px] text-slate-500 text-center">+{dayJobs.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayList({ title, date, jobs, providerName }) {
  return (
    <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <CalendarDays className="w-4 h-4 text-[#5BA83F]" />
        <h2 className="font-heading font-semibold text-lg">{title} · {format(date, "EEEE, MMM d")}</h2>
      </div>
      <div className="space-y-2">
        {jobs.length === 0 && <p className="text-sm text-slate-500">No jobs scheduled.</p>}
        {jobs.map((j) => <JobCard key={j.id} job={j} providerName={providerName} />)}
      </div>
    </div>
  );
}

function JobCard({ job, providerName }) {
  // Color rules per status (Admin schedule glance) — colors convey status, no text label needed
  const s = job.status || "";
  let cls = "bg-slate-50 hover:bg-slate-100 text-slate-800";
  let timeCls = "text-slate-500";
  if (s === "Confirmed" || s === "Cleaner Assigned" || s === "In Progress") {
    cls = "bg-[#E8F5E0] hover:bg-[#DBEDD0] text-[#1F4D0F] ring-1 ring-[#5BA83F]/30";
  } else if (s === "Payment Pending") {
    cls = "bg-amber-100 hover:bg-amber-200 text-amber-900 ring-1 ring-amber-300";
  } else if (s === "Completed") {
    cls = "bg-slate-600 hover:bg-slate-700 text-white ring-1 ring-slate-700";
    timeCls = "text-slate-200";
  }

  const durationHrs = Number(job.estimated_hours) || 0;
  const startTime = job.start_time || "—";
  const endTime = computeEndTime(startTime, durationHrs);
  const fullAddress = [job.address, job.city].filter(Boolean).join(", ");

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`rounded-md px-2.5 py-1.5 transition cursor-default ${cls}`} data-testid={`schedule-job-${job.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold truncate">{job.customer_name}</div>
              <div className={`text-xs font-mono shrink-0 ${timeCls}`}>{startTime}</div>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[280px] bg-white text-slate-800 ring-1 ring-slate-200 shadow-lg p-3" data-testid={`schedule-job-tooltip-${job.id}`}>
          <div className="font-heading font-bold text-[#1B3A6B]">{job.customer_name}</div>
          <div className="mt-1.5 grid grid-cols-[80px_1fr] gap-y-1 text-xs">
            <span className="text-slate-500 font-semibold uppercase tracking-wide">Address</span>
            <span className="text-slate-800">{fullAddress || "—"}</span>
            <span className="text-slate-500 font-semibold uppercase tracking-wide">Time</span>
            <span className="text-slate-800">{startTime}{endTime ? ` – ${endTime}` : ""}</span>
            <span className="text-slate-500 font-semibold uppercase tracking-wide">Duration</span>
            <span className="text-slate-800">{durationHrs ? `${durationHrs}h` : "—"}</span>
            {job.service_type && (
              <>
                <span className="text-slate-500 font-semibold uppercase tracking-wide">Service</span>
                <span className="text-slate-800">{job.service_type}</span>
              </>
            )}
            {providerName && job.assigned_provider_id && (
              <>
                <span className="text-slate-500 font-semibold uppercase tracking-wide">Cleaner</span>
                <span className="text-slate-800">{providerName(job.assigned_provider_id)}</span>
              </>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function computeEndTime(start, hours) {
  if (!start || !hours) return "";
  const m = /^(\d{1,2}):(\d{2})/.exec(start);
  if (!m) return "";
  const total = parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + Math.round(hours * 60);
  const h = Math.floor((total / 60) % 24);
  const mn = total % 60;
  return `${String(h).padStart(2, "0")}:${String(mn).padStart(2, "0")}`;
}
